import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { WORKS } from "@/data/works";
import OptImg from "@/components/OptImg";
import { useT } from "@/i18n/LanguageContext";

// Placeholder cards shown until at least one work is tagged with series: "Ribbon".
// Static (non-clickable) per artist direction.
const PLACEHOLDERS = Array.from({ length: 6 }, (_, i) => ({
  key: `ribbon-placeholder-${i + 1}`,
  label: String(i + 1).padStart(2, "0"),
  image: `/works/placeholder-0${i + 1}.svg`,
  alt: `Ribbon Series placeholder ${i + 1} — image forthcoming.`,
}));

export default function RibbonSeries() {
  const { t } = useT();
  const ribbonWorks = useMemo(() => WORKS.filter((w) => w.series === "Ribbon"), []);
  const hasReal = ribbonWorks.length > 0;

  // Cards to render: real ribbon works if any, otherwise 6 static placeholders.
  const cards = hasReal ? ribbonWorks : PLACEHOLDERS;

  const trackRef = useRef(null);

  // Outer container is tall (200vh per additional card). We measure scroll
  // progress across that range and translate the horizontal track accordingly.
  const outerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: outerRef,
    offset: ["start start", "end end"],
  });

  // Range: [0, 1] -> [0%, -(100% - viewport-widthish)]. Because we can't easily
  // read the track's real pixel width inside useTransform, we express it as a
  // css calc via percentages. The track is width = cards * (card + gap); using
  // -85% gives a comfortable end for 6-8 cards while leaving a trailing gutter.
  // Track width is roughly (card_width + gap) * N. Cards are 26vw wide with a
  // 2rem gap (~2vw). For N cards, the track spans ~ (28 * N)vw. We need to
  // pan by (trackWidth - 100vw + trailing gutter) to reveal the final card.
  const endShift = -(Math.max(0, cards.length * 28 - 90));
  const xVw = useTransform(scrollYProgress, [0, 1], [0, endShift]);
  const x = useSpring(xVw, { stiffness: 90, damping: 22, mass: 0.5 });
  const xTemplate = useTransform(x, (v) => `${v}vw`);

  // The site's html/body use overflow-x: hidden which breaks position: sticky
  // (Lenis smooth-scroll setup). We emulate pinning manually by translating an
  // absolutely-positioned inner container by (scrollYProgress * (sectionHeight - vh))
  // so it appears fixed at viewport top while progress is 0→1.
  const scrollRange = cards.length * 40; // vh — sectionHeight - viewportHeight (== outer height - 100vh)
  const pinY = useTransform(scrollYProgress, [0, 1], [0, scrollRange]);
  const pinTemplate = useTransform(pinY, (v) => `${v}vh`);

  return (
    <section
      id="ribbon-series"
      data-testid="ribbon-series-section"
      ref={outerRef}
      className="relative w-full bg-bone text-ink"
      style={{ height: `${100 + cards.length * 40}vh` }}
    >
      <motion.div
        data-testid="ribbon-pin"
        style={{ y: pinTemplate }}
        className="absolute inset-x-0 top-0 h-screen overflow-hidden flex flex-col will-change-transform"
      >
        {/* Section header — matches Section 03 pattern */}
        <div className="px-6 md:px-10 pt-[10vh] shrink-0">
          <div className="flex items-end justify-between border-b border-ink/25 pb-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase" data-testid="ribbon-series-eyebrow">
              {t("ribbon.eyebrow")}
            </p>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/60">
              {hasReal
                ? t("ribbon.count", { n: cards.length })
                : t("ribbon.hint")}
            </p>
          </div>
          <motion.h2
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 font-serif font-black uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter"
          >
            {t("ribbon.headline_pre")}{" "}
            <em className="font-serif italic font-normal text-crimson">
              {t("ribbon.headline_emph")}
            </em>
            .
          </motion.h2>
        </div>

        {/* Desktop horizontal filmstrip (pinned scroll → x-transform) */}
        <div className="hidden md:flex flex-1 items-center overflow-hidden">
          <motion.div
            ref={trackRef}
            data-testid="ribbon-track"
            style={{ x: xTemplate }}
            className="flex items-center gap-8 pl-10 pr-[10vw] will-change-transform"
          >
            {cards.map((c, i) => (
              <RibbonCard key={c.slug || c.key} card={c} index={i} isReal={hasReal} />
            ))}
          </motion.div>
        </div>

        {/* Mobile fallback — native horizontal swipe carousel */}
        <div className="md:hidden flex-1 flex items-center overflow-x-auto snap-x snap-mandatory scroll-smooth">
          <div className="flex items-center gap-4 pl-6 pr-[20vw]">
            {cards.map((c, i) => (
              <div key={c.slug || c.key} className="snap-start">
                <RibbonCard card={c} index={i} isReal={hasReal} mobile />
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function RibbonCard({ card, index, isReal, mobile = false }) {
  const width = mobile ? "w-[72vw]" : "w-[26vw] max-w-[420px] min-w-[260px]";
  const height = mobile ? "h-[52vh]" : "h-[62vh]";

  const media = (
    <div className={`relative overflow-hidden bg-ink/5 ${width} ${height}`}>
      <OptImg
        src={card.image}
        alt={card.alt || card.title}
        sizes="(min-width: 768px) 30vw, 72vw"
        className="w-full h-full object-cover"
      />
      <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.3em] uppercase text-ink/70 bg-bone/70 px-2 py-1 backdrop-blur-sm">
        № {isReal ? String(index + 1).padStart(2, "0") : card.label}
      </div>
    </div>
  );

  const meta = (
    <div className={`mt-3 ${width}`}>
      <h3 className="font-serif text-lg md:text-xl leading-tight">
        {isReal ? card.title : "Ribbon Series"}
      </h3>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/55 mt-1">
        {isReal ? `${card.medium} · ${card.year}` : "Placeholder · image forthcoming"}
      </p>
    </div>
  );

  if (isReal) {
    return (
      <Link
        to={`/works/${card.slug}`}
        data-cursor="view"
        data-testid={`ribbon-card-${card.slug}`}
        className="block shrink-0 group"
      >
        {media}
        {meta}
      </Link>
    );
  }

  // Static (non-clickable) until tagged with series: "Ribbon".
  return (
    <div
      data-testid={`ribbon-card-${card.key}`}
      className="block shrink-0"
      aria-label={card.alt}
    >
      {media}
      {meta}
    </div>
  );
}
